// NB The ticket that is sent back from a successful mutation does not match the data sent in
//    fields that are undefined will show up with null values in the server response for eg
//    this comparer takes those differences into account, it does not do a straight-up comparison

import { ExtendTicket, TicketExtended, TicketLineItemExtended, TicketLocationExtended } from "./TypeExtensions";
import { Signable, Ticket, TicketPax } from "../sharedCode/avroGraphql";
import dayjs from "dayjs";
import { isChargeLineItem, isFuelLineItem, isLandingLineItem, isMinimumsLineItem, isPerDiemLineItem } from "../Components/validation/newHelpers";

export const lineItemMatches = (a: TicketLineItemExtended, b: TicketLineItemExtended): boolean => {
    if (isFuelLineItem(a)) {
        return a.line_item_type_id === b.line_item_type_id && stringsMatch(a.comments, b.comments) && (a.amount ?? 0) === (b.amount ?? 0);
    }

    if (isMinimumsLineItem(a)) {
        return a.line_item_type_id === b.line_item_type_id && stringsMatch(a.comments, b.comments) && a.amount === b.amount;
    }

    if (isChargeLineItem(a)) {
        return a.line_item_type_id === b.line_item_type_id && stringsMatch(a.comments, b.comments) && (a.quantity ?? 0) === (b.quantity ?? 0);
    }

    if (isLandingLineItem(a)) {
        return a.line_item_type_id === b.line_item_type_id && stringsMatch(a.comments, b.comments) && (a.quantity ?? 0) === (b.quantity ?? 0);
    }

    if (isPerDiemLineItem(a)) {
        return a.line_item_type_id === b.line_item_type_id && stringsMatch(a.comments, b.comments) && (a.quantity ?? 0) === (b.quantity ?? 0);
    }

    return a.line_item_type_id === b.line_item_type_id && stringsMatch(a.comments, b.comments) && a.quantity === b.quantity;
};

export const locationMatches = (a: TicketLocationExtended, b: TicketLocationExtended): boolean => {
    return (
        stringsMatch(a.location, b.location) &&
        stringsMatch(a.cost_code, b.cost_code) &&
        a.hours === b.hours &&
        dateTimesMatch(a.up_time, b.up_time) &&
        dateTimesMatch(a.down_time, b.down_time)
    );
};

export function paxMatches(a: TicketPax, b: TicketPax): boolean {
    return stringsMatch(a.name, b.name);
}

export function signaturesMatch(a: Signable, b: Signable): boolean {
    return (
        a.signable_resource_type_id === b.signable_resource_type_id &&
        stringsMatch(a.signatory, b.signatory) &&
        stringsMatch(a.signatory_job_title, b.signatory_job_title) &&
        stringsMatch(a.company, b.company) &&
        stringsMatch(a.signatory_email, b.signatory_email)
    );
}

// TODO dates are stored w millisecs locally, come back truncated to seconds
// signatures.model & emails should not be uppercased on load from server

const FIELDS_TO_EXCLUDE = ["id", "ticket_id", "inputValue", "inputHours", "is_selected", "Job", "LineItemType", "OpenedCards", "ResourceType", "keyHash", "ref_id", "__typename"];

function shouldProcessField(key: string) {
    const localFields = ExtendTicket({} as Ticket);
    const isTicketField = !(key in localFields);
    const isNotExcludedField = FIELDS_TO_EXCLUDE.findIndex((x) => x === key) < 0;
    return isTicketField && isNotExcludedField;
}

function isBoolean(key: string, value: any): boolean {
    const BOOLEAN_FIELDS = ["is_averaged", "is_minimum", "see_passenger_manifest", "signature_pending"];
    if (BOOLEAN_FIELDS.findIndex((fieldName) => fieldName === key) >= 0) {
        return true;
    }
    return typeof value === "boolean";
}

function isList(value: any): boolean {
    return hasValue(value) && typeof value === "object";
}

function isString(value: any): boolean {
    return typeof value === "string";
}

function isDateField(key: string) {
    return ["date", "completed_date"].findIndex((field) => field === key) >= 0;
}

function isTimeField(key: string) {
    return ["up_time", "down_time"].findIndex((field) => field === key) >= 0;
}

function compareValues(key: string, outputKey: string, localValue: any, serverValue: any, diff: any) {
    const hasLocalValue = hasValue(localValue);
    const hasServerValue = hasValue(serverValue);

    if (!hasLocalValue && !hasServerValue) return;

    if (isTimeField(key) || isDateField(key)) {
        if (hasLocalValue && !dateTimesMatch(localValue, serverValue)) {
            diff[outputKey] = { local: localValue, server: serverValue };
        }
    } else if (isBoolean(key, localValue)) {
        if (hasLocalValue) {
            if (typeof localValue === "boolean") {
                if (localValue !== (serverValue === 1)) {
                    diff[outputKey] = { local: localValue, server: serverValue };
                }
            } else if (localValue !== serverValue) {
                diff[outputKey] = { local: localValue, server: serverValue };
            }
        } else if (serverValue === 1) {
            // a null bool comes back as 0
            diff[outputKey] = { local: localValue, server: serverValue };
        }
    } else if (hasLocalValue && isString(localValue)) {
        if (!stringsMatch(localValue, serverValue)) {
            diff[outputKey] = { local: localValue, server: serverValue };
        }
    } else if (localValue !== serverValue) {
        diff[outputKey] = { local: localValue, server: serverValue };
    }
}
const hasValue = (value: any) => {
    return value != null && value != undefined;
};

function stringsMatch(a: string | null | undefined, b: string | null | undefined) {
    if (hasValue(a) && hasValue(b)) {
        return a?.toLowerCase() === b?.toLowerCase();
    }

    return !hasValue(a) && !hasValue(b);
}

function dateTimesMatch(a: string | null | undefined, b: string | null | undefined) {
    if (hasValue(a) && hasValue(b)) {
        return dayjs(a).isSame(dayjs(b), "second");
    }

    return !hasValue(a) && !hasValue(b);
}

// have to compare each list type separately
// the local IDs have been replaced when the server response is returned so we have to use a (type-specific) business key comparison
// we have 4 list types - line items, locations, pax, signatures
export const compareLineItems = (localList: Record<string, unknown>, serverList: Record<string, unknown>): Record<string, unknown> => {
    const result = {} as Record<string, unknown>;
    let counter = 0;

    const local = localList as unknown as Array<TicketLineItemExtended>;
    const server = serverList as unknown as Array<TicketLineItemExtended>;

    for (let i = 0; i < local.length; i++) {
        const target = local[i];
        if (server.findIndex((l) => lineItemMatches(l, target)) < 0) {
            result[`LineItems.${counter++}`] = { local: `${target.line_item_type_id} ${target.comments}` };
        }
    }

    for (let i = 0; i < server.length; i++) {
        const target = server[i];
        if (local.findIndex((l) => lineItemMatches(l, target)) < 0) {
            result[`LineItems.${counter++}`] = { server: `${target.line_item_type_id} ${target.comments}` };
        }
    }

    return result;
};

export const compareLocations = (localList: Record<string, unknown>, serverList: Record<string, unknown>): Record<string, unknown> => {
    const result = {} as Record<string, unknown>;
    let counter = 0;

    const local = localList as unknown as Array<TicketLocationExtended>;
    const server = serverList as unknown as Array<TicketLocationExtended>;

    for (let i = 0; i < local.length; i++) {
        const target = local[i];
        if (server.findIndex((l) => locationMatches(l, target)) < 0) {
            result[`Locations.${counter++}`] = { local: `${target.location} ${target.cost_code} ${target.hours} ${target.up_time} ${target.down_time}` };
        }
    }

    for (let i = 0; i < server.length; i++) {
        const target = server[i];
        if (local.findIndex((l) => locationMatches(l, target)) < 0) {
            result[`Locations.${counter++}`] = { server: `${target.location} ${target.cost_code} ${target.hours} ${target.up_time} ${target.down_time}` };
        }
    }

    return result;
};

export const comparePax = (localList: Record<string, unknown>, serverList: Record<string, unknown>): Record<string, unknown> => {
    const result = {} as Record<string, unknown>;
    let counter = 0;

    const local = localList as unknown as Array<TicketPax>;
    const server = serverList as unknown as Array<TicketPax>;

    for (let i = 0; i < local.length; i++) {
        const target = local[i];
        if (server.findIndex((l) => paxMatches(l, target)) < 0) {
            result[`Pax.${counter++}`] = { local: `${target.name}` };
        }
    }

    for (let i = 0; i < server.length; i++) {
        const target = server[i];
        if (local.findIndex((l) => paxMatches(l, target)) < 0) {
            result[`Pax.${counter++}`] = { server: `${target.name}` };
        }
    }

    return result;
};

export const compareSignatures = (localList: Record<string, unknown>, serverList: Record<string, unknown>): Record<string, unknown> => {
    const result = {} as Record<string, unknown>;
    let counter = 0;

    const local = localList as unknown as Array<Signable>;
    const server = serverList as unknown as Array<Signable>;

    for (let i = 0; i < local.length; i++) {
        const target = local[i];
        if (server.findIndex((l) => signaturesMatch(l, target)) < 0) {
            result[`Signatures.${counter++}`] = {
                local: `${target.signable_resource_type_id} ${target.signatory} ${target.signatory_job_title} ${target.company} ${target.signatory_email}`,
            };
        }
    }

    for (let i = 0; i < server.length; i++) {
        const target = server[i];
        if (local.findIndex((l) => signaturesMatch(l, target)) < 0) {
            result[`Signatures.${counter++}`] = {
                server: `${target.signable_resource_type_id} ${target.signatory} ${target.signatory_job_title} ${target.company} ${target.signatory_email}`,
            };
        }
    }

    return result;
};

export function compareLists(fieldName: string, localValue: Record<string, unknown>, serverValue: Record<string, unknown>): Record<string, unknown> {
    let listResult = {};
    switch (fieldName) {
        case "LineItems":
            listResult = compareLineItems(localValue as Record<string, unknown>, serverValue as Record<string, unknown>);
            break;
        case "Locations":
            listResult = compareLocations(localValue as Record<string, unknown>, serverValue as Record<string, unknown>);
            break;
        case "Pax":
            listResult = comparePax(localValue as Record<string, unknown>, serverValue as Record<string, unknown>);
            break;
        case "Signatures":
            listResult = compareSignatures(localValue as Record<string, unknown>, serverValue as Record<string, unknown>);
            break;
        default:
        // do nothing
    }
    return listResult;
}

export const validateMergedTicket = (localTicket: TicketExtended, serverTicket: Ticket, mergedTicket: TicketExtended): Record<string, unknown> => {
    // we have to assume the merge worked, so as long as one of these tickets compares with no differences we are good
    const localDifferences = validateServerResponse(localTicket, mergedTicket);
    if (Object.keys(localDifferences).length === 0) {
        return localDifferences;
    }
    return validateServerResponse(mergedTicket, serverTicket);
};

export const validateServerResponse = (localTicket: TicketExtended, serverTicket: Ticket): Record<string, unknown> => {
    const result: Record<string, unknown> = {};

    for (const fieldName in localTicket) {
        if (shouldProcessField(fieldName)) {
            const outputKey = fieldName;
            const localValue = (localTicket as Record<string, unknown>)[fieldName];
            const serverValue = serverTicket !== undefined && fieldName in serverTicket ? (serverTicket as Record<string, unknown>)[fieldName] : undefined;

            if (isList(localValue)) {
                const listResult = compareLists(fieldName, localValue as Record<string, unknown>, serverValue as Record<string, unknown>);

                if (Object.keys(listResult).length > 0) {
                    Object.assign(result, listResult);
                }
            } else {
                // we have two values, need to compare them in various ways
                compareValues(fieldName, outputKey, localValue, serverValue, result);
            }
        }
    }

    // only care about fields that aren't on localTicket, everything else is processed already
    for (const fieldName in serverTicket) {
        if (shouldProcessField(fieldName)) {
            const outputKey = fieldName;

            if (!(fieldName in localTicket)) {
                const serverValue = (serverTicket as Record<string, unknown>)[fieldName];
                if (isList(serverValue)) {
                    // we have two lists, need to recursively process them
                    const nestedDiff = compareLists(fieldName, {}, serverValue as Record<string, unknown>);
                    if (Object.keys(nestedDiff).length > 0) {
                        Object.assign(result, nestedDiff);
                    }
                } else {
                    // we have two values, need to compare them in various ways
                    compareValues(fieldName, outputKey, undefined, serverValue, result);
                }
            }
        }
    }

    return result;
};
